import { createAccessControl } from 'better-auth/plugins';
import { first } from 'lodash';
import type { UserRole } from '@/db/schema';

export const ac = createAccessControl({
  auth: ['me', 'sign-out', 'change-password', 'force-change-password'],
  dashboard: ['browse'],
  // Check-in no portão: 'browse' abre a tela, 'create' identifica o recruta (dá número e confirma pagamento).
  'check-in': ['browse', 'create'],
  // Página "Meu Perfil" — qualquer usuário autenticado acessa a própria (o `AppLayout`
  // deriva o recurso do 1º segmento da URL pra todo mundo em `_app`, então precisa existir
  // aqui senão `can()` nega geral, admin incluso).
  profile: ['browse'],
  // Tela da galeria (`/gallery`) — os dados vêm de `albums`/`photos`, que têm permissões próprias.
  gallery: ['browse'],
  users: ['browse', 'create', 'read', 'update', 'delete'],
  screens: ['browse', 'create', 'read', 'update', 'delete'],
  uploads: ['browse', 'create'],
  bataillons: ['browse', 'create', 'read', 'update', 'delete'],
  soldiers: ['browse', 'create', 'read', 'update', 'delete'],
  events: ['browse', 'create', 'read', 'update', 'delete'],
  'term-versions': ['browse', 'create', 'read', 'update', 'delete'],
  'term-acceptances': ['browse', 'create', 'read', 'update', 'delete'],
  albums: ['browse', 'create', 'read', 'update', 'delete'],
  photos: ['browse', 'create', 'read', 'update', 'delete'],
  // Importar posts do Instagram na galeria: 'browse' lista/conecta contas, 'create' importa, 'delete' desconecta.
  instagram: ['browse', 'create', 'delete'],
  testimonials: ['browse', 'create', 'read', 'update', 'delete'],
} as const);

/**
 * Preset "acesso completo" — ponto único que o scaffolder (`create-resource.ts`) atualiza
 * ao gerar um recurso novo. Cada papel abaixo usa este preset por padrão, mas pode divergir
 * a qualquer momento (troque `ac.newRole(fullAccess)` por um objeto próprio no papel que
 * precisar de permissões diferentes dos demais).
 */
const fullAccess = {
  auth: Object.values(ac.statements.auth),
  dashboard: Object.values(ac.statements.dashboard),
  'check-in': Object.values(ac.statements['check-in']),
  profile: Object.values(ac.statements.profile),
  gallery: Object.values(ac.statements.gallery),
  users: Object.values(ac.statements.users),
  screens: Object.values(ac.statements.screens),
  uploads: Object.values(ac.statements.uploads),
  bataillons: Object.values(ac.statements['bataillons']),
  soldiers: Object.values(ac.statements['soldiers']),
  events: Object.values(ac.statements['events']),
  'term-versions': Object.values(ac.statements['term-versions']),
  'term-acceptances': Object.values(ac.statements['term-acceptances']),
  albums: Object.values(ac.statements['albums']),
  photos: Object.values(ac.statements['photos']),
  instagram: Object.values(ac.statements.instagram),
  testimonials: Object.values(ac.statements['testimonials']),
};

/** Preset "acesso limitado" — mesma ideia do `fullAccess`, ponto único pro scaffolder. */
const limitedAccess = {
  auth: Object.values(ac.statements.auth),
  dashboard: Object.values(ac.statements.dashboard),
  'check-in': [],
  profile: Object.values(ac.statements.profile),
  gallery: [],
  screens: ['browse', 'read'],
  users: [],
  uploads: ['browse', 'create'],
  bataillons: ['browse', 'read'],
  soldiers: ['browse', 'read'],
  events: ['browse', 'read'],
  'term-versions': ['browse', 'read'],
  'term-acceptances': ['browse', 'read'],
  albums: ['browse', 'read'],
  photos: ['browse', 'read'],
  instagram: [],
  // Curadoria de testemunhos: só acesso completo (como Galeria/Check-in).
  testimonials: [],
} as const;

/**
 * Papéis com acesso administrativo completo por padrão (usado nos poucos lugares que ainda
 * precisam de uma checagem binária admin/não-admin, ex.: quem pode resetar senha de outro
 * usuário). Permissões por recurso de verdade são as de `roles` acima, configuráveis por
 * papel individualmente — isto aqui é só pra atalhos binários pontuais.
 */
export const ADMIN_TIER_ROLES: UserRole[] = [
  'marechal',
  'general',
  'major',
  'capitao',
  'tenente',
];

export function isAdminTierRole(role: UserRole | string | undefined | null): boolean {
  return !!role && (ADMIN_TIER_ROLES as string[]).includes(role);
}

export const roles = {
  marechal: ac.newRole(fullAccess),
  general: ac.newRole(fullAccess),
  major: ac.newRole(fullAccess),
  capitao: ac.newRole(fullAccess),
  tenente: ac.newRole(fullAccess),
  sargento: ac.newRole(limitedAccess),
  soldado: ac.newRole(limitedAccess),
};

/**
 * Checa permissão para recursos compilados (users/auth/dashboard/screens), presentes
 * no `ac` estático do better-auth. Este módulo é isomórfico (usado no client e no server),
 * então NÃO pode depender do banco — recursos dinâmicos (criados via editor de screens)
 * são resolvidos só no servidor, por `canWithDynamic` em `src/api/core/permissions.server.ts`.
 */
export function can(role: UserRole, resource: string, action: string) {
  const roleDef = roles[role];

  if (!roleDef) return false;

  if (!(resource in ac.statements)) return false;

  const { success } = (roleDef.authorize as any)({
    [resource]: [action],
  });

  return success;
}

export const findResourceAndAction = (path: string, method: string) => {
  path = path.replace(/^\/api\/v1\//, '');
  const resource = first(path.split('/')) ?? '';

  let action = '';

  if (resource === 'auth') {
    action = path.split('/')[1]!;
  } else {
    if (method === 'GET') action = 'browse';
    else if (method === 'POST') action = 'create';
    else if (method === 'PUT') action = 'update';
    else if (method === 'PATCH') action = 'update';
    else if (method === 'DELETE') action = 'delete';
  }

  // console.log(`${resource}:${action}`);
  return {
    resource,
    action,
  };
};
